import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    CustomCollapseCard,
    CustomDatePicker,
    CustomForm,
    CustomFormItem,
    CustomInput,
    CustomSelect,
    errorDialog,
    Option,
    Text,
    useText,
} from '@src/components';
import { VouIdCode, monthsList, orderTypeList } from '@src/store/enums';
import dayjs from '@src/utils/day';
import '@src/styles/balanceUpload/addInformation.scss';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import 'dayjs/locale/tr';
import { loadBalancesUpdate } from '@src/store/slice/balanceUploadSlice';
dayjs.locale('tr');

const SharedAdditionalInformation = ({ form, sendFormToParentPage = null, vouId, callSendOnInit = false }) => {
    const { currentLoadBalance, runTimeTotalPrice } = useSelector((state) => state?.balanceUpload);
    const [isDateControl, setIsDateControl] = useState(false);
    const [formValActivationDate, setFormValActivationDate] = useState(null);
    const currentDate = dayjs();
    const eBillInformation = useText('enterYourAdditionalInformationRegardingEInvoice');
    const selectChoose = useText('choose');
    const changeDateRef = useRef(false);
    const dispatch = useDispatch();
    const balanceActivationDateRequired = useText('balanceActivationDateRequired');
    const youCannotEnterAPastDate = useText('youCannotEnterAPastDate');
    const invoiceWillBeIssuedInMonth = useText('invoiceWillBeIssuedInMonth');

    useEffect(async () => {
        const currentMonth = currentDate?.month() + 1;
        const existNextMonth = true;
        setIsDateControl(existNextMonth);

        const loadBalanceInvoiceMonth =
            currentLoadBalance?.invoiceMonth !== 0 ? currentLoadBalance?.invoiceMonth : undefined;
        const invoiceMonth = !existNextMonth ? currentMonth : loadBalanceInvoiceMonth || currentMonth;

        let nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;
        let filterData = monthsList.filter((item) => [currentMonth, nextMonth].includes(item.value));
        if (filterData.length === 1) {
            setIsDateControl(false);
        }
        const activationDate = dayjs(currentLoadBalance?.activationDate);
        let isBefore =
            activationDate.isBefore(currentDate, 'day') ||
            (!changeDateRef.current && activationDate.isSame(currentDate, 'day'));
        if (currentLoadBalance?.pendingApproval) {
            isBefore = activationDate.isBefore(currentDate, 'day');
        }

        form.setFieldsValue({
            invoiceMonth: invoiceMonth,
            eInvoiceInformation: currentLoadBalance?.eInvoiceInformation,
            orderType: currentLoadBalance?.orderType,
            activationDate: isBefore ? null : activationDate,
        });
        const formVal = await form.getFieldsValue();
        if (callSendOnInit && sendFormToParentPage) sendFormToParentPage(formVal);
        setFormValActivationDate(formVal.activationDate);
    }, [currentLoadBalance, form, isDateControl]);

    const disabledDate = useCallback((current) => {
        if (!current) return false;

        const now = dayjs();
        const nextMonthEnd = now.add(1, 'month').endOf('month');

        return current.isBefore(now.startOf('day')) || current.isAfter(nextMonthEnd);
    }, []);

    const pastDateCheckValidator = async (_field, value) => {
        if (value === null && runTimeTotalPrice === 0) {
            errorDialog({
                title: <Text t="balanceApproval" />,
                message: order.continueInstruction,
            });
            return Promise.resolve();
        }
        if (value !== null) {
            try {
                const now = new Date();
                const today = dayjs(now.toDateString());
                const selectedDate = dayjs(value);
                const isBeforeDay = selectedDate.isBefore(today, 'day');
                if (isBeforeDay) {
                    errorDialog({
                        title: <Text t="error" />,
                        message: youCannotEnterAPastDate,
                    });
                    return Promise.reject(new Error('error'));
                }
                return Promise.resolve();
            } catch (e) {
                console.error(e);
                return Promise.reject(new Error('error'));
            }
        } else {
            errorDialog({
                title: <Text t="error" />,
                message: balanceActivationDateRequired,
            });
            return Promise.resolve();
        }
    };

    const activationDateCheckValidator = async (_field, value) => {
        if (value !== null) {
            try {
                const values = await form.getFieldsValue();
                let sysDateYear = new Date().getFullYear();
                let sysDateMonth = new Date().getMonth() + 1;

                if (value.month() + 1 === values.invoiceMonth && value.year() === sysDateYear) {
                    return Promise.resolve();
                }
                if (value.month() === values.invoiceMonth && value.year() === sysDateYear) {
                    return Promise.resolve();
                }

                if (
                    sysDateMonth === 12 &&
                    values.invoiceMonth === 1 &&
                    value.month() + 1 === values.invoiceMonth &&
                    value.year() === sysDateYear + 1
                ) {
                    return Promise.resolve();
                }

                return Promise.reject(new Error('error'));
            } catch (e) {
                console.error(e);
                return Promise.reject(new Error('error'));
            }
        } else {
            return Promise.resolve();
        }
    };

    const handleActivationDateChange = async (value) => {
        if (!value) return;
        changeDateRef.current = true;
        const activationDateIso = dayjs(value)
            .utcOffset(0)
            .set('hour', 9)
            .set('minute', 0)
            .set('second', 0)
            .toISOString();
        await dispatch(
            loadBalancesUpdate({
                ...currentLoadBalance,
                invoiceMonth: dayjs(value).month() + 1,
                activationDate: activationDateIso,
                orderStatus: 0,
                vouId: vouId || VouIdCode.Market,
            }),
        );
        if (sendFormToParentPage) sendFormToParentPage(form);
        if (currentLoadBalance?.invoiceMonth !== dayjs(value).month() + 1) {
            await form.validateFields(['activationDate', 'invoiceMonth']);
        }
    };

    return (
        <CustomCollapseCard
            cardTitle={useText('completeYourOrderInformation')}
            cardTitleAlign="center"
            titleColor="#1d2228"
            titleSize="16px"
            showExpandIcon={false}
        >
            <div className="add-information-container">
                <CustomForm
                    name="additionalInformation"
                    className="addInformationForm"
                    initialValues={{
                        orderType: 0,
                    }}
                    autoComplete="off"
                    layout={'horizontal'}
                    form={form}
                >
                    <div className="form-item-compliment ">
                        <CustomFormItem
                            label={useText('invoiceMonth')}
                            name="invoiceMonth"
                            className={'bill-month-form-item'}
                            style={{ display: 'none' }}
                            rules={[{ required: true, message: <Text t="requiredField2" /> }]}
                        ></CustomFormItem>
                        <div className="form-item-compliment2 ">
                            <CustomFormItem
                                label={useText('dateWhenBalanceWillBeAvailable')}
                                name="activationDate"
                                className="active-date-form-item"
                                rules={[
                                    { required: true, message: <Text t="thisFieldCannotBeLeftBlank" /> },
                                    {
                                        validator: pastDateCheckValidator,
                                        message: <Text t="youCannotEnterAPastDate" />,
                                    },
                                    {
                                        validator: activationDateCheckValidator,
                                        message: <Text t="balanceDateAndInvoiceMonthMustBeCompatible" />,
                                    },
                                ]}
                            >
                                <CustomDatePicker
                                    id="ticketRestaurant-balanceUpload-balanceInformation-additionalInformation-activationDate-datePicker"
                                    placeholder={useText('pickDate')}
                                    onChange={handleActivationDateChange}
                                    disabledDate={disabledDate}
                                    allowClear={false}
                                />
                            </CustomFormItem>
                        </div>
                        <CustomFormItem
                            label={useText('orderType')}
                            name="orderType"
                            className={'order-type-form-item'}
                        >
                            <CustomSelect
                                id="ticketRestaurant-cardOrders-cardInformation-additionalInformation-orderType-select"
                                placeholder={selectChoose}
                                style={{ width: 160 }}
                            >
                                {orderTypeList.map((orderType) => (
                                    <Option
                                        id={`ticketRestaurant-cardOrders-cardInformation-additionalInformation-orderType-${orderType?.text}-option`}
                                        key={orderType.value}
                                        value={orderType.value}
                                    >
                                        {orderType.text}
                                    </Option>
                                ))}
                            </CustomSelect>
                        </CustomFormItem>
                    </div>

                    {formValActivationDate && (
                        <div className="compliment-notification-div ">
                            {invoiceWillBeIssuedInMonth.replace('{{month}}', dayjs(formValActivationDate).format('MMMM'))}
                        </div>
                    )}
                    <div className="form-item-compliment ">
                        <CustomFormItem label={<Text t="invoiceAddress2" />} className={'delivery-address-form-item'}>
                            <span className="address-info-box">{currentLoadBalance?.invoiceAddress}</span>
                        </CustomFormItem>

                        <CustomFormItem
                            label={useText('eInvoiceAdditionalInfo')}
                            name="eInvoiceInformation"
                            className={'delivery-address-add-form-item'}
                        >
                            <CustomInput
                                id="ticketRestaurant-balanceUpload-balanceInformation-additionalInformation-eInvoiceInformation-textInput"
                                placeholder={eBillInformation}
                                maxLength={150}
                                hasNumberChar={true}
                            />
                        </CustomFormItem>
                    </div>
                </CustomForm>
            </div>
        </CustomCollapseCard>
    );
};

SharedAdditionalInformation.propTypes = {
    form: PropTypes.any,
    sendFormToParentPage: PropTypes.func,
    vouId: PropTypes.number,
    callSendOnInit: PropTypes.bool,
};

export default SharedAdditionalInformation;
